import React, { useState } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Edit, Trash2 } from 'lucide-react';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';

const localizer = momentLocalizer(moment);

export const CalendarView = ({ schedules, owners, pets, doctors, onEdit, onDelete }) => {
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');

  // Transform schedules to events for the calendar
  const events = schedules.map((schedule) => {
    const start = new Date(schedule.schedule_datetime);
    const end = moment(start).add(30, 'minutes').toDate();

    return {
      id: schedule.id,
      title: schedule.description,
      start,
      end,
      resource: schedule,
    };
  });
  const getOwnerName = (ownerId) => owners.find((o) => o.id === ownerId)?.full_name || 'Unknown';
  const getPetName = (petId) => pets.find((p) => p.id === petId)?.name || 'Unknown';
  const getDoctorName = (doctorId) => doctors.find((d) => d.id === doctorId)?.full_name || 'Not assigned';

  const handleSelectEvent = (event) => {
    setSelectedSchedule(event.resource);
    setConfirmingDelete(false);
    setIsModalOpen(true);
  };

  const handleSelectSlot = () => {
    // Optional: could open add schedule dialog with pre-filled date
  };

  return (
    <>
      <div className="bg-white rounded-3xl shadow-lg p-8 calendar-container">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 600 }}
          date={currentDate}
          view={currentView}
          onNavigate={(date) => setCurrentDate(date)}
          onView={(view) => setCurrentView(view)}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          popup
          className="custom-calendar"
        />
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Schedule Details</DialogTitle>
          </DialogHeader>
          {selectedSchedule && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-bold text-sage-800 mb-2">{selectedSchedule.description}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Pet</p>
                  <p className="font-semibold text-gray-800">{getPetName(selectedSchedule.pet_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Owner</p>
                  <p className="font-semibold text-gray-800">{getOwnerName(selectedSchedule.owner_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Doctor</p>
                  <p className="font-semibold text-gray-800">Dr. {getDoctorName(selectedSchedule.doctor_id)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date & Time</p>
                  <p className="font-semibold text-gray-800">
                    {new Date(selectedSchedule.schedule_datetime).toLocaleString()}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500">Notes</p>
                <p className="text-gray-800">{selectedSchedule.description}</p>
              </div>
              <div className="flex gap-2 pt-4">
                <Button
                  onClick={() => {
                    onEdit(selectedSchedule);
                    setIsModalOpen(false);
                  }}
                  className="rounded-full bg-sage-500 hover:bg-sage-600 text-white flex-1"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                {confirmingDelete ? (
                  <div className="flex gap-2 flex-1">
                    <Button
                      onClick={() => {
                        onDelete(selectedSchedule.id);
                        setIsModalOpen(false);
                      }}
                      className="rounded-full bg-red-600 hover:bg-red-700 text-white flex-1"
                      size="sm"
                    >
                      Confirm
                    </Button>
                    <Button
                      onClick={() => setConfirmingDelete(false)}
                      className="rounded-full flex-1"
                      variant="outline"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setConfirmingDelete(true)}
                    className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 flex-1"
                    variant="outline"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
